import { useBreakpoints } from "@hooks";
import { color } from "@styles";
import { FourWorlds, Forest, OceanWaves, Desert, Mountains } from "components/atoms/Environments";
import { HeroSection } from "components/organisms/HeroSection";
import { Clouds } from "./Clouds";
import { File, Image, Text, types } from "react-bricks/frontend";
import styled from "styled-components";
import { backgroundColorProp } from "../utils/backgroundColor";
import { useLottie } from "hooks/useLottie";
import TextBlock from "../common/TextBlock";
import LottieUploadedImage from "../common/LottieUploadedImage";
import Button from "../common/Button";

enum BackgroundEnvironment {
  None = "None",
  FourWorlds = "Four worlds",
  Forest = "Forest",
  OceanWaves = "Ocean waves",
  Desert = "Desert",
  Mountains = "Mountains",
}

interface Props {
  title: string;
  ctaLabel: string;
  showClouds?: boolean;
  showButton?: boolean;
  environment?: BackgroundEnvironment;
  image?: types.IImageSource;
  showEyebrow?: boolean;
  backgroundColor?: types.IColor;
  cloudColor?: types.IColor;
  text?: string;
  subheading?: string;
  ctaLink?: string;
  imagePosition: "center" | "grounded";
  imageType: "hidden" | "image" | "lottie-static" | "lottie-remote";
  lottieStaticUrl?: string;
  imageZoom?: number;
  imageVerticalOffset?: number;
  showPreButtonImage?: boolean;
  showPostButtonImage?: boolean;
  preButtonImageWidth?: number;
  postButtonImageWidth?: number;
}

//=============================
// Component to be rendered
//=============================
const HeroLanding: types.Brick<Props> = ({
  title,
  ctaLabel,
  showClouds,
  environment,
  showEyebrow,
  backgroundColor,
  cloudColor,
  subheading,
  imageType,
  imagePosition,
  ctaLink = "",
  lottieStaticUrl,
  imageZoom,
  showButton,
  imageVerticalOffset,
  showPreButtonImage,
  showPostButtonImage,
  preButtonImageWidth = 180,
  postButtonImageWidth = 300,
}) => {
  const { isDesktop } = useBreakpoints();
  const img = (
    <Zoom zoom={imageZoom} imageVerticalOffset={imageVerticalOffset}>
      <RightImage imageType={imageType} lottieStaticUrl={lottieStaticUrl} />
    </Zoom>
  );

  const titleComponent = (
    <Text
      propName="title"
      value={title}
      placeholder="Type a title..."
      renderBlock={({ children }) => <span>{children}</span>}
    />
  );

  const subheadingComponent = (
    <Text
      propName="subheading"
      value={subheading}
      placeholder="Type a subheading..."
      renderBlock={({ children }) => <span>{children}</span>}
      renderPlaceholder={() => <></>}
    />
  );

  const eyebrowComponent = showEyebrow ? (
    <Text
      propName="eyeBrow"
      value={subheading}
      placeholder="Type an eyebrow..."
      renderBlock={({ children }) => <span>{children}</span>}
      renderPlaceholder={() => <></>}
    />
  ) : null;

  const buttonComponent = showButton ? <Button text={ctaLabel} href={ctaLink} /> : null;

  const preButtonImageComponent = showPreButtonImage ? (
    <PreButtonImageWrapper width={preButtonImageWidth}>
      <Image propName="preButtonImage" alt="image" maxWidth={preButtonImageWidth} />
    </PreButtonImageWrapper>
  ) : null;

  const postButtonImageComponent = showPostButtonImage ? (
    <PostButtonWrapper>
      <PostButtonImageWrapper width={postButtonImageWidth}>
        <Image propName="postButtonImage" alt="image" maxWidth={postButtonImageWidth} />
      </PostButtonImageWrapper>
    </PostButtonWrapper>
  ) : null;

  const additionalCopy = (
    <>
      {preButtonImageComponent}
      {buttonComponent}
    </>
  );

  return (
    <HeroSection
      title={titleComponent}
      eyeBrow={eyebrowComponent}
      groundedImg={imagePosition === "grounded"}
      subHeading={subheadingComponent}
      image={img}
      clouds={showClouds ? <Clouds color={cloudColor?.color} /> : undefined}
      background={getEnvironment(environment)}
      copyPadding={isDesktop ? "5.5rem 1rem 8rem 1rem" : null}
      backgroundColor={backgroundColor?.color}
      buttonText={undefined}
      buttonLink={undefined}
      textPt="6rem"
      additionalCopy={additionalCopy}
      lowerSection={postButtonImageComponent}
    ></HeroSection>
  );
};

const RightImage = ({
  imageType,
  lottieStaticUrl,
}: Pick<Props, "imageType" | "lottieStaticUrl">) => {
  if (imageType === "hidden") {
    return null;
  }

  if (imageType === "lottie-static") {
    return <Lottie url={lottieStaticUrl} />;
  }

  if (imageType === "lottie-remote") {
    return <LottieUploadedImage propName="lottieUrl" maxWidth="440px" />;
  }

  if (imageType === "image") {
    return (
      <ImageWrapper>
        <Image
          propName="image"
          alt="image"
          maxWidth={440}
          containerStyle={{ position: "relative" }}
          imageStyle={{ maxWidth: 440, height: "auto" }}
        />
      </ImageWrapper>
    );
  }
};

const getEnvironment = (option: BackgroundEnvironment) => {
  switch (option) {
    case BackgroundEnvironment.None:
      return null;
    case BackgroundEnvironment.FourWorlds:
      return <FourWorlds />;
    case BackgroundEnvironment.Forest:
      return <Forest />;
    case BackgroundEnvironment.OceanWaves:
      return <OceanWaves />;
    case BackgroundEnvironment.Desert:
      return <Desert />;
    case BackgroundEnvironment.Mountains:
      return <Mountains />;
    default:
      return null;
  }
};

const Lottie = ({ url }: { url: string }) => {
  const ref = useLottie(url);
  return <ImageWrapper ref={ref} />;
};

const Zoom = styled.div<{ zoom: number; imageVerticalOffset: number }>`
  ${(props) => (props.zoom ? `zoom: ${props.zoom};` : ``)};
  ${(props) =>
    props.imageVerticalOffset ? `margin-top: ${props.imageVerticalOffset * -1}px` : ``};
`;

const ImageWrapper = styled.div`
  max-width: 440px;
`;

const PreButtonImageWrapper = styled.div<{ width: number }>`
  max-width: ${(p) => p.width}px;
  margin-bottom: 2.5rem;
  img {
    width: 100%;
    height: auto;
  }
`;

const PostButtonImageWrapper = styled.div<{ width: number }>`
  max-width: ${(p) => p.width}px;
  img {
    width: 100%;
    height: auto;
  }
`;

const PostButtonWrapper = styled.div`
  margin-top: 3rem;
  padding-bottom: 0.75rem;
`;

const Upload = styled.div`
  width: 200px;
  height: 300px;
  background-color: #f0f0f0;
`;

HeroLanding.schema = {
  name: "hero-landing",
  label: "Hero Landing (Yuniverse)",
  previewImageUrl: `/static/bricks/hero-landing.png`,
  getDefaultProps: () => ({
    title: "We're on a mission to inspire people to live their best lives every day",
    subheading: "You can add a subtitle here... or delete it",
    imagePosition: "grounded",
    imageType: "image",
    ctaLabel: "Click me",
    showClouds: true,
    environment: BackgroundEnvironment.FourWorlds,
    image: {
      src: "/static/giraffes/YugiClimbingMountain.svg",
    },
    preButtonImage: {
      src: "/static/logos/YuLifeXBupa.png",
    },
    postButtonImage: {
      src: "/static/logos/MetLife.svg",
    },
  }),
  sideEditProps: [
    {
      groupName: "Background",
      defaultOpen: true,
      props: [
        {
          name: "showClouds",
          label: "Show Clouds?",
          type: types.SideEditPropType.Boolean,
        },
        {
          name: "cloudColor",
          label: "Cloud Color",
          type: types.SideEditPropType.Select,
          shouldRefreshStyles: true,
          show: (props) => props.showClouds,
          selectOptions: {
            display: types.OptionsDisplay.Color,
            options: [
              { value: { color: color.white }, label: "White" },
              { value: { color: color.S320 }, label: "Purple" },
              { value: { color: color.S220 }, label: "Blue" },
              { value: { color: color.S120 }, label: "Green" },
              { value: { color: color.S430 }, label: "Yellow" },
            ],
          },
        },
        backgroundColorProp,
        {
          name: "environment",
          label: "World",
          type: types.SideEditPropType.Select,
          shouldRefreshStyles: true,
          selectOptions: {
            display: types.OptionsDisplay.Select,
            options: Object.keys(BackgroundEnvironment).map((key) => ({
              label: BackgroundEnvironment[key as keyof typeof BackgroundEnvironment],
              value: key,
            })),
          },
        },
      ],
    },
    {
      groupName: "Content",
      defaultOpen: true,
      props: [
        {
          name: "showEyebrow",
          label: "Show eyebrow?",
          shouldRefreshStyles: true,
          type: types.SideEditPropType.Boolean,
        },
        {
          name: "showPreButtonImage",
          label: "Show pre-button image?",
          shouldRefreshStyles: true,
          type: types.SideEditPropType.Boolean,
        },
        {
          name: "preButtonImageWidth",
          label: "Pre-Button image width",
          show: (props) => props.showPreButtonImage,
          type: types.SideEditPropType.Range,
          shouldRefreshStyles: true,
          rangeOptions: {
            min: 50,
            max: 400,
            step: 5,
          },
        },
        {
          name: "showButton",
          label: "Show button?",
          shouldRefreshStyles: true,
          type: types.SideEditPropType.Boolean,
        },
        {
          name: "showPostButtonImage",
          label: "Show post-button image?",
          shouldRefreshStyles: true,
          type: types.SideEditPropType.Boolean,
        },
        {
          name: "postButtonImageWidth",
          label: "Post-Button image width",
          show: (props) => props.showPostButtonImage,
          type: types.SideEditPropType.Range,
          shouldRefreshStyles: true,
          rangeOptions: {
            min: 50,
            max: 400,
            step: 5,
          },
        },
        {
          name: "ctaLabel",
          label: "Button Label",
          type: types.SideEditPropType.Text,
          show: (props) => props.showButton,
        },
        {
          name: "ctaLink",
          label: "Button Link",
          type: types.SideEditPropType.Text,
          show: (props) => props.showButton,
        },
      ],
    },
    {
      groupName: "Right Image",
      defaultOpen: true,
      props: [
        {
          name: "imageType",
          label: "Right Image Type",
          type: types.SideEditPropType.Select,
          shouldRefreshStyles: true,
          selectOptions: {
            display: types.OptionsDisplay.Select,
            options: [
              { label: "None", value: "hidden" },
              { label: "Image", value: "image" },
              { label: "Lottie (pre-loaded)", value: "lottie-static" },
              { label: "Lottie (upload new)", value: "lottie-remote" }, // Note: this is off for now until we sort the JSON CORS issue
            ],
          },
        },
        {
          name: "imagePosition",
          label: "Right Image Position",
          type: types.SideEditPropType.Select,
          shouldRefreshStyles: true,
          show: (props) => props.imageType !== "hidden",
          selectOptions: {
            display: types.OptionsDisplay.Select,
            options: [
              { label: "Centered", value: "centered" },
              { label: "Bottom", value: "grounded" },
            ],
          },
        },
        {
          name: "lottieStaticUrl",
          label: "Lottie Asset",
          type: types.SideEditPropType.Select,
          shouldRefreshStyles: true,
          show: (props) => props.imageType === "lottie-static",
          selectOptions: {
            display: types.OptionsDisplay.Select,
            options: [
              { label: "Yugi", value: "/static/lottie/Yugi.json" },
              { label: "YugiCinema", value: "/static/lottie/YugiCinema.json" },
              { label: "YugiCycling", value: "/static/lottie/YugiCycling.json" },
              { label: "YugiData", value: "/static/lottie/YugiData.json" },
              { label: "YugiDesk", value: "/static/lottie/YugiDesk.json" },
              { label: "YugiEarth", value: "/static/lottie/YugiEarth.json" },
              { label: "YugiGLI", value: "/static/lottie/YugiGLI.json" },
              { label: "YugiGiraffeShopping", value: "/static/lottie/YugiGiraffeShopping.json" },
              { label: "YugiMeasuring", value: "/static/lottie/YugiMeasuring.json" },
              { label: "YugiMeditate", value: "/static/lottie/YugiMeditate.json" },
              { label: "YugiNewspaper", value: "/static/lottie/YugiNewspaper.json" },
              {
                label: "YugiNewspaperGrounded",
                value: "/static/lottie/YugiNewspaperGrounded.json",
              },
              { label: "YugiPopping", value: "/static/lottie/YugiPopping.json" },
              { label: "YugiPoppingJapan", value: "/static/lottie/YugiPoppingJapan.json" },
              { label: "YugiRocket", value: "/static/lottie/YugiRocket.json" },
              { label: "YugiSuperhero", value: "/static/lottie/YugiSuperhero.json" },
              { label: "YugiTrophy", value: "/static/lottie/YugiTrophy.json" },
              { label: "YugiXray", value: "/static/lottie/YugiXray.json" },
              {
                label: "yugi-giraffe-hugging-purple-phone",
                value: "/static/lottie/yugi-giraffe-hugging-purple-phone.json",
              },
              {
                label: "yulife-employee-yumoji",
                value: "/static/lottie/yulife-employee-yumoji.json",
              },
              { label: "yulife-yugi-esg", value: "/static/lottie/yulife-yugi-esg.json" },
              { label: "yulife-yugi-yustore", value: "/static/lottie/yulife-yugi-yustore.json" },
              { label: "global-rewards", value: "/static/lottie/GlobalRewards.json" },
              { label: "toothbrush", value: "/static/lottie/ToothyTickled.json" },
            ],
          },
        },
        {
          name: "imageZoom",
          label: "Adjust image size",
          type: types.SideEditPropType.Range,
          shouldRefreshStyles: true,
          show: (props) => props.imageType !== "hidden",
          rangeOptions: {
            min: 0.2,
            max: 2,
            step: 0.05,
          },
        },
        {
          name: "imageVerticalOffset",
          label: "Adjust image offset",
          type: types.SideEditPropType.Range,
          shouldRefreshStyles: true,
          show: (props) => props.imageType !== "hidden",
          rangeOptions: {
            min: -100,
            max: 100,
            step: 5,
          },
        },
      ],
    },
  ],
};

export default HeroLanding;
